import { HttpError } from "ra-core";

import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import type {
  Catalogs,
  ContactMatch,
  CrmDataProvider,
  Dashboard,
  DetailsResource,
  LinkType,
  NewContact,
  NewDeal,
  NewIdentifier,
  NewLink,
  NewParty,
  NewTask,
  SearchResult,
  Timeline,
  TimelineResource,
} from "../types";
import { getValidSession, isCognitoConfigured } from "./cognito";
import { getStubCustomerId, getStubRole } from "./principal";
import { runtimeConfig } from "./runtimeConfig";
import { getWorkspace } from "./workspace";

async function authHeaders(
  extra: Record<string, string> = {},
): Promise<HeadersInit> {
  if (!isCognitoConfigured()) {
    // Local BFF only: the stub header stands in for a principal.
    return {
      "x-ardley-customer-id": getStubCustomerId(),
      "x-ardley-role": getStubRole(),
      "x-ardley-workspace": getWorkspace(),
      ...extra,
    };
  }
  const session = await getValidSession();
  if (!session) {
    // A 401 makes ra-core's checkError log out and send the user to login.
    throw new HttpError("not_authenticated", 401);
  }
  return {
    authorization: `Bearer ${session.idToken}`,
    "x-ardley-workspace": getWorkspace(),
    ...extra,
  };
}

async function request<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const headers = await authHeaders(
    init.body === undefined ? {} : { "content-type": "application/json" },
  );
  const res = await fetch(`${runtimeConfig().apiUrl}${path}`, {
    method: init.method ?? "GET",
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const message =
      (body && typeof body.error === "string" && body.error) ||
      `${init.method ?? "GET"} ${path} failed (${res.status})`;
    throw new HttpError(message, res.status, body);
  }
  return (await res.json()) as T;
}

/** Resources the BFF lists, and whether it pages them. */
const LIST_ROUTES: Record<string, { path: string; paged: boolean }> = {
  contacts: { path: "/contacts", paged: true },
  companies: { path: "/companies", paged: true },
  deals: { path: "/deals", paged: true },
  pipelines: { path: "/pipelines", paged: false },
  pipeline_stages: { path: "/pipeline-stages", paged: false },
  saved_views: { path: "/saved-views", paged: false },
};

const SHOW_ROUTES: Record<string, string> = {
  contacts: "/contacts",
  companies: "/companies",
  deals: "/deals",
};

/**
 * Anything the BFF does not serve fails loudly. Returning empty lists or fake
 * success made stock Atomic screens look working while they did nothing.
 */
function notInPoc(what: string): never {
  throw new HttpError(`${what} is not available in this POC`, 501);
}

export interface Me {
  customerId: string;
  tenantUuid: string;
  /** crm_users.id: the id rows store as owner_id (invariant 3). */
  ownerId: string;
  email: string | null;
  fullName: string;
  /** The platform role, and what it may change (W9 F4, permissions.mjs). */
  role: string | null;
  permissions: string[];
  /** The workspaces this user may open, and the one in use (0025, W15 M9). */
  workspaces?: { id: string; slug: string; name: string; kind: string }[];
  workspace?: string | null;
}

/** The BFF's view of the signed-in principal. */
export function fetchMe(): Promise<Me> {
  return request<Me>("/me");
}

export const getDataProvider = (): CrmDataProvider =>
  ({
    getList: async (resource, params) => {
      const route = LIST_ROUTES[resource];
      if (!route) notInPoc(`Listing ${resource}`);
      const filter = params?.filter ?? {};
      const query = new URLSearchParams();
      for (const key of ["q", "first_name", "last_name", "pipeline_id"]) {
        if (filter[key]) query.set(key, String(filter[key]));
      }
      if (route.paged && params?.pagination) {
        const { page, perPage } = params.pagination;
        query.set("limit", String(perPage));
        query.set("offset", String((page - 1) * perPage));
      }
      const qs = query.toString();
      const body = await request<{
        data: Record<string, unknown>[];
        total: number;
      }>(qs ? `${route.path}?${qs}` : route.path);
      return { data: body.data, total: body.total };
    },
    getOne: async (resource, params) => {
      const base = SHOW_ROUTES[resource];
      if (!base) notInPoc(`Showing ${resource}`);
      const body = await request<{ data: Record<string, unknown> }>(
        `${base}/${params.id}`,
      );
      return { data: body.data };
    },
    getMany: async (resource) => notInPoc(`Fetching ${resource} by id`),
    getManyReference: async (resource) =>
      notInPoc(`Fetching related ${resource}`),
    create: async (resource) => notInPoc(`Creating ${resource}`),
    update: async (resource, params) => {
      if (resource !== "deals") notInPoc(`Editing ${resource}`);
      const body = await request<{ data: Record<string, unknown> }>(
        `/deals/${params.id}`,
        { method: "PATCH", body: { stage_id: params.data.stage_id } },
      );
      return { data: { ...params.previousData, ...params.data, ...body.data } };
    },
    updateMany: async (resource) => notInPoc(`Editing ${resource}`),
    delete: async (resource) => notInPoc(`Deleting ${resource}`),
    deleteMany: async (resource) => notInPoc(`Deleting ${resource}`),
    // Timeline, notes and tasks (W8 D2).
    getTimeline: async (resource: TimelineResource, id: string) =>
      (await request<{ data: Timeline }>(`/${resource}/${id}/timeline`)).data,
    addNote: (resource: TimelineResource, id: string, body: string) =>
      request(`/${resource}/${id}/notes`, { method: "POST", body: { body } }),
    editNote: (noteId: string, body: string) =>
      request(`/notes/${noteId}`, { method: "PATCH", body: { body } }),
    addTask: (resource: TimelineResource, id: string, task: NewTask) =>
      request(`/${resource}/${id}/tasks`, { method: "POST", body: task }),
    setTaskCompleted: (taskId: string, completed: boolean) =>
      request(`/tasks/${taskId}`, { method: "PATCH", body: { completed } }),
    getDashboard: async () =>
      (await request<{ data: Dashboard }>("/dashboard")).data,
    search: async (q: string) =>
      (
        await request<{ data: SearchResult[] }>(
          `/search?${new URLSearchParams({ q })}`,
        )
      ).data,
    getCatalogs: async () =>
      (await request<{ data: Catalogs }>("/catalogs")).data,
    createContact: async (contact: NewContact) =>
      (
        await request<{ data: { id: string } }>("/contacts", {
          method: "POST",
          body: contact,
        })
      ).data,
    // The same name, or any of the identifiers, already in the tenant.
    findContactMatches: async ({ first_name, last_name, identifiers }) => {
      const matches = new Map<string, ContactMatch>();
      if (first_name.trim() && last_name.trim()) {
        const qs = new URLSearchParams({
          first_name: first_name.trim(),
          last_name: last_name.trim(),
        });
        const { data } = await request<{
          data: { id: string; first_name: string; last_name: string }[];
        }>(`/contacts?${qs}`);
        for (const row of data) {
          matches.set(row.id, {
            id: row.id,
            name: `${row.first_name} ${row.last_name}`,
            reason: "name",
          });
        }
      }
      for (const { id_type, value } of identifiers) {
        if (!value.trim()) continue;
        const qs = new URLSearchParams({ type: id_type, value: value.trim() });
        const { data } = await request<{
          data: { contact_id: string; first_name: string; last_name: string }[];
        }>(`/identifiers?${qs}`);
        for (const row of data) {
          if (!matches.has(row.contact_id)) {
            matches.set(row.contact_id, {
              id: row.contact_id,
              name: `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim(),
              reason: id_type,
            });
          }
        }
      }
      return [...matches.values()];
    },
    addIdentifier: (contactId: string, identifier: NewIdentifier) =>
      request(`/contacts/${contactId}/identifiers`, {
        method: "POST",
        body: identifier,
      }),
    removeIdentifier: (identifierId: string) =>
      request(`/identifiers/${identifierId}`, { method: "DELETE" }),
    createDeal: async (deal: NewDeal) =>
      (
        await request<{ data: { id: string } }>("/deals", {
          method: "POST",
          body: deal,
        })
      ).data,
    addParty: (dealId: string, party: NewParty) =>
      request(`/deals/${dealId}/parties`, { method: "POST", body: party }),
    endParty: (partyId: string) =>
      request(`/deal-parties/${partyId}`, {
        method: "PATCH",
        body: { ended: true },
      }),
    createCompany: async (company) =>
      (
        await request<{ data: { id: string } }>("/companies", {
          method: "POST",
          body: company,
        })
      ).data,
    updateCompany: (id, changes) =>
      request(`/companies/${id}`, { method: "PATCH", body: changes }),
    addAffiliation: (membership) =>
      request("/affiliations", { method: "POST", body: membership }),
    endAffiliation: (affiliationId: string) =>
      request(`/affiliations/${affiliationId}`, {
        method: "PATCH",
        body: { ended: true },
      }),
    getLinkTypes: async () =>
      (await request<{ data: LinkType[] }>("/link-types")).data,
    createLink: (link: NewLink) =>
      request("/links", { method: "POST", body: link }),
    endLink: (linkId: string) =>
      request(`/links/${linkId}`, { method: "PATCH", body: { ended: true } }),
    updateDetails: (
      resource: DetailsResource,
      id: string,
      body: Record<string, unknown>,
    ) => request(`/${resource}/${id}`, { method: "PATCH", body }),
    mergeContacts: async (loserId: string, winnerId: string) => {
      return request<{ data: { winner_id: string } }>("/contacts/merge", {
        method: "POST",
        body: { loser_id: loserId, winner_id: winnerId },
      });
    },
    getConfiguration: async () => ({}) as ConfigurationContextValue,
    isInitialized: async () => true,
  }) as CrmDataProvider;
